'use client'

import { useEffect, useMemo, useSyncExternalStore } from 'react'
import {
	getServerSnapshot,
	getSnapshot,
	subscribe,
	want,
	type MarkQuote,
	type QuoteState
} from './store'

/** The whole live quote state. Prefer the narrower hooks below. */
export function useQuotes(): QuoteState {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

const getBtcSnapshot = () => getSnapshot().btc?.usd ?? null
const getServerBtcSnapshot = () => null

/** Bitcoin's live USD price, or null before the first tick. */
export function useBtcUsd(): number | null {
	return useSyncExternalStore(subscribe, getBtcSnapshot, getServerBtcSnapshot)
}

/**
 * Live fair marks for a set of collections. Registers interest for as long
 * as the component is mounted, so the stream carries these collections.
 */
export function useMarks(slugs: readonly string[]): Readonly<Record<string, MarkQuote>> {
	const key = slugs.filter(Boolean).slice().sort().join(',')
	useEffect(() => {
		if (!key) {
			return
		}
		return want(key.split(','))
	}, [key])
	const marks = useQuotes().marks
	return useMemo(() => {
		const out: Record<string, MarkQuote> = {}
		for (const s of key ? key.split(',') : []) {
			if (marks[s]) {
				out[s] = marks[s]
			}
		}
		return out
	}, [key, marks])
}

/** One collection's live fair mark, or null until the hub has one. */
export function useMark(slug: string | null | undefined): MarkQuote | null {
	const marks = useMarks(slug ? [slug] : [])
	return slug ? marks[slug] ?? null : null
}

/**
 * What an item is worth: the fair value or the OW floor, whichever is
 * lower. The fair value is where an ordinary piece has been clearing; the
 * floor is what one can be bought for right now. Worth is the lesser --
 * nobody pays the clearing price when a listing sits under it -- and a
 * listing priced at it sells. Either alone when only one is known.
 */
export function lowerQuote(
	fair: number | null | undefined,
	floor: number | null | undefined
): number | null {
	const f = typeof fair === 'number' && Number.isFinite(fair) && fair > 0 ? fair : null
	const l = typeof floor === 'number' && Number.isFinite(floor) && floor > 0 ? floor : null
	if (f !== null && l !== null) return Math.min(f, l)
	return f ?? l
}
